import type { MouseEvent } from 'react';
import type { Node, NodeProps } from '@xyflow/react';
import { RectangleStackIcon } from '@heroicons/react/24/outline';
import { MinusIcon, PlusIcon } from '@heroicons/react/16/solid';
import { Badge } from '../../../catalyst/badge';
import { GROUP_PAGE } from '../graphModel';
import type { FlowNodeData } from '../useGraphLayout';
import { CollapseButton } from './CollapseButton';
import { KIND_PALETTE } from '../kindPalette';
import { NodeShell } from './NodeShell';

const count = (value: number) => value.toLocaleString('en-US');

/**
 * A group of a node's children of one kind or document category; a click shows its members, `−` hides them,
 * and `+30` shows the next page while members remain.
 */
export function GroupNode({ data }: NodeProps<Node<FlowNodeData>>) {
  if (data.node.kind !== 'group') return null;
  const { label, total, shown } = data.node;
  const { onCollapse, onShowMore } = data;
  return (
    <NodeShell
      icon={RectangleStackIcon}
      iconClassName="text-gray-500 dark:text-zinc-400"
      compactClassName={KIND_PALETTE.group.compact}
      label={`${label} · ${count(total)}`}
      selected={data.selected}
      focused={data.focused}
      grouping
      className="w-55"
    >
      {/* A long label (a document category) wraps to two lines; the slot in NODE_SIZE is sized for two. */}
      <div className="flex items-start gap-2">
        <span className="line-clamp-2 min-w-0 text-sm font-medium break-words" title={label}>
          {label}
        </span>
        <Badge color="zinc">{count(total)}</Badge>
        <span className="ml-auto flex shrink-0">
          {onCollapse ? (
            <CollapseButton label={label} icon={MinusIcon} onCollapse={() => onCollapse(data.node.id)} />
          ) : (
            data.expandable && <PlusIcon className="size-3.5 text-gray-400 dark:text-zinc-500" />
          )}
        </span>
      </div>
      {data.expanded && (
        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-zinc-400">
          <span>{shown >= total ? 'all shown' : `${count(shown)} of ${count(total)} shown`}</span>
          {onShowMore && (
            <button
              type="button"
              aria-label={`Show ${GROUP_PAGE} more members`}
              title={`Show ${GROUP_PAGE} more`}
              className="nodrag ml-auto rounded px-1 font-medium text-indigo-600 hover:bg-zinc-100 dark:text-indigo-400 dark:hover:bg-zinc-700"
              onClick={(event: MouseEvent) => {
                // The card's click handler would select the group again.
                event.stopPropagation();
                onShowMore(data.node.id);
              }}
            >
              +{GROUP_PAGE}
            </button>
          )}
        </div>
      )}
    </NodeShell>
  );
}
